import type { Rng } from '@src/core/Rng';
import { DoorKind, GateSide } from '@src/game/arena/RoomLayout';
import { FloorModifier, rollFloorModifier } from '@src/game/FloorModifiers';

/** Opening floor is the classic Smash TV 5x3; later floors draw from FLOOR_SHAPES. */
export const FLOOR_COLS = 5;
export const FLOOR_ROWS = 3;
/** Last playable floor (one tour of the six themed courts). Clearing its Exit archmage wins. */
export const FINAL_FLOOR = 6;

export function isFinalFloor(floor: number): boolean {
  return floor >= FINAL_FLOOR;
}
/** Largest grid any floor can be (typed arrays are sized to this). */
export const FLOOR_MAX_COLS = 7;
export const FLOOR_MAX_ROWS = 4;

interface FloorShape {
  cols: number;
  rows: number;
  /** Earliest floor this shape can roll on. */
  fromFloor: number;
  weight: number;
}

/** Grid shapes by floor. Bigger grids come later so a run keeps opening up. */
const FLOOR_SHAPES: readonly FloorShape[] = [
  { cols: 5, rows: 3, fromFloor: 1, weight: 3 },
  { cols: 4, rows: 3, fromFloor: 2, weight: 2 },
  { cols: 6, rows: 3, fromFloor: 2, weight: 2.5 },
  { cols: 5, rows: 4, fromFloor: 3, weight: 2 },
  { cols: 7, rows: 3, fromFloor: 3, weight: 1.5 },
  { cols: 6, rows: 4, fromFloor: 4, weight: 1.5 },
  { cols: 7, rows: 4, fromFloor: 6, weight: 1 },
];

/** Grid delta per gate side: north is up (row - 1), matching the on-screen minimap. */
const SIDE_DELTA: ReadonlyArray<readonly [number, number]> = [
  [0, -1], // North
  [1, 0], // East
  [0, 1], // South
  [-1, 0], // West
];

/**
 * Smash TV style floor: a grid of rooms. Doors lead to grid neighbors, out-of-grid sides
 * are sealed. The entrance is the west-middle cell, the exit sits in the east column.
 * Grid size, the mix of special rooms and the floor rule all vary by floor.
 */
export class FloorMap {
  cols = FLOOR_COLS;
  rows = FLOOR_ROWS;
  readonly kinds = new Uint8Array(FLOOR_MAX_COLS * FLOOR_MAX_ROWS);
  readonly cleared = new Uint8Array(FLOOR_MAX_COLS * FLOOR_MAX_ROWS);
  readonly visited = new Uint8Array(FLOOR_MAX_COLS * FLOOR_MAX_ROWS);
  col = 0;
  row = 1;
  /** 1-based floor number. */
  floor = 0;
  /** Rule in force on this floor (FloorModifier). */
  modifier: FloorModifier = FloorModifier.None;
  /** Set when the exit room is cleared; RoomDirector consumes it. */
  exitCleared = false;

  index(col: number, row: number): number {
    return row * this.cols + col;
  }

  get current(): number {
    return this.index(this.col, this.row);
  }

  /** Number of live cells on this floor (kinds/cleared/visited beyond this are unused). */
  get cellCount(): number {
    return this.cols * this.rows;
  }

  /** Entrance row: the middle of the west column. */
  get entranceRow(): number {
    return Math.floor(this.rows / 2);
  }

  currentKind(): DoorKind {
    return this.kinds[this.current] as DoorKind;
  }

  /** True while standing in the room the floor was entered through. */
  isAtEntrance(): boolean {
    return this.col === 0 && this.row === this.entranceRow;
  }

  /** Lay out a fresh floor. Guarantees Combat majority and one Exit reachable from the entrance. */
  generate(rng: Rng): void {
    this.floor++;
    const shape = this.pickShape(rng);
    this.cols = shape.cols;
    this.rows = shape.rows;
    this.kinds.fill(DoorKind.Combat);
    this.cleared.fill(0);
    this.visited.fill(0);
    this.col = 0;
    this.row = this.entranceRow;
    this.exitCleared = false;
    this.modifier = rollFloorModifier(rng, this.floor, this.modifier);

    const exitRow = rng.int(0, this.rows - 1);
    this.kinds[this.index(this.cols - 1, exitRow)] = DoorKind.Exit;

    // Special rooms scale with the grid so a big floor is not all combat. Never on the
    // entrance or exit; at most ~45% of the grid so combat stays the majority.
    const cells = this.cellCount;
    const budget = Math.floor(cells * 0.45);
    const wants: DoorKind[] = [];
    const treasure = cells >= 20 ? 3 : 2;
    const hazard = cells >= 24 ? 3 : cells >= 15 ? 2 : 1;
    for (let i = 0; i < treasure; i++) wants.push(DoorKind.Treasure);
    for (let i = 0; i < hazard; i++) wants.push(DoorKind.Hazard);
    if (this.floor >= 2 && rng.chance(0.7)) wants.push(DoorKind.Arena);
    if (this.floor >= 2 && rng.chance(0.65)) wants.push(DoorKind.Gauntlet);
    if (this.floor >= 3 && (cells >= 18 || rng.chance(0.5))) wants.push(DoorKind.Sanctum);
    if (this.floor >= 5 && cells >= 24 && rng.chance(0.5)) wants.push(rng.chance(0.5) ? DoorKind.Arena : DoorKind.Gauntlet);
    while (wants.length > budget) wants.pop();

    let guard = 0;
    while (wants.length > 0 && guard++ < 256) {
      const c = rng.int(0, this.cols - 1);
      const r = rng.int(0, this.rows - 1);
      const i = this.index(c, r);
      if ((c === 0 && r === this.entranceRow) || this.kinds[i] !== DoorKind.Combat) continue;
      const kind = wants.pop() as DoorKind;
      // A rest room right beside the entrance is wasted; push sanctums past the first column.
      if (kind === DoorKind.Sanctum && c === 0) {
        wants.push(kind);
        continue;
      }
      this.kinds[i] = kind;
    }
    this.visited[this.current] = 1;
  }

  private pickShape(rng: Rng): FloorShape {
    if (this.floor <= 1) return FLOOR_SHAPES[0];
    let total = 0;
    for (let i = 0; i < FLOOR_SHAPES.length; i++) if (FLOOR_SHAPES[i].fromFloor <= this.floor) total += FLOOR_SHAPES[i].weight;
    let roll = rng.next() * total;
    for (let i = 0; i < FLOOR_SHAPES.length; i++) {
      const s = FLOOR_SHAPES[i];
      if (s.fromFloor > this.floor) continue;
      roll -= s.weight;
      if (roll <= 0) return s;
    }
    return FLOOR_SHAPES[0];
  }

  neighbor(side: GateSide): number {
    const [dc, dr] = SIDE_DELTA[side];
    const c = this.col + dc;
    const r = this.row + dr;
    if (c < 0 || c >= this.cols || r < 0 || r >= this.rows) return -1;
    return this.index(c, r);
  }

  /** What the gate on `side` leads to (Sealed at the grid edge). */
  doorKind(side: GateSide): DoorKind {
    const n = this.neighbor(side);
    return n === -1 ? DoorKind.Sealed : (this.kinds[n] as DoorKind);
  }

  /** Step through a gate. Returns false if that side is sealed. */
  move(side: GateSide): boolean {
    const n = this.neighbor(side);
    if (n === -1) return false;
    const [dc, dr] = SIDE_DELTA[side];
    this.col += dc;
    this.row += dr;
    this.visited[n] = 1;
    return true;
  }

  markCleared(): void {
    this.cleared[this.current] = 1;
    if (this.currentKind() === DoorKind.Exit) this.exitCleared = true;
  }

  clearedCount(): number {
    let n = 0;
    const cells = this.cellCount;
    for (let i = 0; i < cells; i++) n += this.cleared[i];
    return n;
  }
}
